// pdf.js in the main thread, configured like the test harness: no
// system fonts, data files from our own versioned vendor folder.
import * as pdfjs from 'pdfjs-dist';
import paths from '../generated/vendor-paths.json';

const base = (p: string) => new URL(p, document.baseURI).href;
pdfjs.GlobalWorkerOptions.workerSrc = base(`${paths.pdfjs}pdf.worker.min.mjs`);

// One pdf.js worker for the whole session, started at load: fetching it lazily
// would be a network request after the app has loaded (AC-PRIV-1).
const pdfWorker = new pdfjs.PDFWorker();
export const pdfjsReady: Promise<unknown> = pdfWorker.promise;

export type PdfjsDocument = pdfjs.PDFDocumentProxy;

export interface ViewerHandle {
  promise: Promise<PdfjsDocument>;
  /** frees the document and its worker-side resources */
  destroy(): Promise<void>;
}

export const PasswordException = pdfjs.PasswordException;

export function openForViewing(bytes: Uint8Array, password?: string): ViewerHandle {
  const task = pdfjs.getDocument({
    data: bytes.slice(), // pdf.js transfers (detaches) its input
    password,
    cMapUrl: base(`${paths.pdfjs}cmaps/`),
    cMapPacked: true,
    standardFontDataUrl: base(`${paths.pdfjs}standard_fonts/`),
    wasmUrl: base(`${paths.pdfjs}wasm/`),
    iccUrl: base(`${paths.pdfjs}iccs/`),
    useSystemFonts: false,
    enableXfa: false,
    worker: pdfWorker,
  });
  return { promise: task.promise, destroy: () => task.destroy() };
}

/**
 * Renders a page into `canvas` at `scale` CSS px per point (canvas pixels: times
 * `opts.ratio`, by default the screen's); `done` gives the viewport. `cancel` stops the render and frees the canvas at once (pdf.js
 * refuses a second render into a canvas that is still in use), so the page can
 * be drawn again right away, e.g. when React runs an effect twice.
 */
/** The most pixels a page's canvas is given on screen (pdf.js' own viewer uses the same), and its longest side. */
const MAX_CANVAS_PIXELS = 2 ** 25;
const MAX_CANVAS_SIDE = 16384;

export function renderPage(doc: PdfjsDocument, pageNumber: number, canvas: HTMLCanvasElement, scale: number, opts: { ratio?: number; annotations?: boolean } = {}): { done: Promise<pdfjs.PageViewport>; cancel: () => void } {
  let cancelled = false;
  let task: pdfjs.RenderTask | undefined;
  const done = (async () => {
    const page = await doc.getPage(pageNumber);
    if (cancelled) throw new pdfjs.RenderingCancelledException(`Rendering cancelled, page ${pageNumber}`);
    const viewport = page.getViewport({ scale });
    // On screen, a page larger than the canvas can hold (a 200-inch page at 100 %) is drawn at a lower resolution
    // and stretched, rather than not at all. A caller that passes its own ratio maps canvas pixels itself (OCR).
    const area = viewport.width * viewport.height;
    const ratio = opts.ratio ?? Math.min(window.devicePixelRatio || 1, Math.sqrt(MAX_CANVAS_PIXELS / area), MAX_CANVAS_SIDE / viewport.width, MAX_CANVAS_SIDE / viewport.height);
    canvas.width = Math.round(viewport.width * ratio);
    canvas.height = Math.round(viewport.height * ratio);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    task = page.render({
      canvas,
      canvasContext: canvas.getContext('2d')!,
      viewport,
      transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
      annotationMode: opts.annotations === false ? pdfjs.AnnotationMode.DISABLE : pdfjs.AnnotationMode.ENABLE,
    });
    await task.promise;
    return viewport;
  })();
  return {
    done,
    cancel: () => {
      cancelled = true;
      task?.cancel();
    },
  };
}
